import React, { useState } from 'react';
import {
  Mail,
  ShieldCheck,
  UserCheck,
  KeyRound,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseService';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { NIGERIAN_STATES, NIGERIAN_LGAS_MAP } from '../../data/nigerianLocations';

export const AdminEmailProActivationCard: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'token' | 'manual'>('token');
  const [activationToken, setActivationToken] = useState('');
  const [approvedPhotoDataUrl, setApprovedPhotoDataUrl] = useState<string>('');
  const [approvedPhotoName, setApprovedPhotoName] = useState<string>('');
  const [temporaryPassword, setTemporaryPassword] = useState('');

  // Manual entry fallback fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+234');
  const [primaryCategory, setPrimaryCategory] = useState('plumber');
  const [state, setState] = useState('Lagos');
  const [lga, setLga] = useState('Ikeja');
  const [businessName, setBusinessName] = useState('');
  const [yearsExperience, setYearsExperience] = useState(5);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activatedResult, setActivatedResult] = useState<{
    fullName: string;
    email: string;
    applicationReference: string;
    temporaryPassword?: string;
  } | null>(null);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setApprovedPhotoDataUrl(String(reader.result || ''));
      setApprovedPhotoName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setActivatedResult(null);
    setSubmitting(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const bodyPayload: any = {
        approvedProfilePhotoDataUrl: approvedPhotoDataUrl || undefined,
        temporaryPassword: temporaryPassword.trim() || undefined,
      };

      if (mode === 'token') {
        if (!activationToken.trim()) {
          setError('Please paste the signed Activation Token from the application notification email.');
          setSubmitting(false);
          return;
        }
        bodyPayload.activationToken = activationToken.trim();
      } else {
        if (!fullName.trim() || !email.trim() || !phone.trim()) {
          setError('Please enter the applicant’s Full Name, Email, and Phone Number from their application email.');
          setSubmitting(false);
          return;
        }
        bodyPayload.manualDetails = {
          fullName: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          primaryCategory,
          state,
          lga,
          businessName: businessName.trim() || `${fullName.trim()} Services`,
          yearsExperience: Number(yearsExperience) || 5,
          serviceCoverageAreas: [lga],
        };
      }

      const res = await fetch('/api/admin/pro-applications/activate', {
        method: 'POST',
        headers,
        body: JSON.stringify(bodyPayload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to activate professional account.');
        return;
      }

      setActivatedResult({
        fullName: data.professional.fullName,
        email: data.professional.email,
        applicationReference: data.professional.applicationReference,
        temporaryPassword: data.temporaryPassword,
      });
      setActivationToken('');
    } catch (err: any) {
      setError(err?.message || 'Network error while activating professional account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-900 border border-emerald-500/30 rounded-3xl p-5 sm:p-6 shadow-xl text-white">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-black text-white flex items-center gap-2">
              <span>Direct Professional Account Activation</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-mono">
                Admin Token Gate
              </span>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Applications are automatically recorded in the verification database and queue. If you have an activation token from a verification notification, you can also activate an account directly here.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer self-start sm:self-center shrink-0"
        >
          <UserCheck className="w-4 h-4" />
          <span>{isOpen ? 'Hide Activation Panel' : 'Activate Reviewed Applicant'}</span>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {isOpen && (
        <form onSubmit={handleActivate} className="mt-5 pt-5 border-t border-slate-800 space-y-4 text-xs">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('token')}
              className={`px-3.5 py-1.5 rounded-xl font-bold cursor-pointer ${
                mode === 'token'
                  ? 'bg-emerald-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              Paste Activation Token from Email
            </button>
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`px-3.5 py-1.5 rounded-xl font-bold cursor-pointer ${
                mode === 'manual'
                  ? 'bg-emerald-500 text-slate-950'
                  : 'bg-slate-800 text-slate-300 hover:text-white'
              }`}
            >
              Manual Operational Entry
            </button>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-700 text-rose-200 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {activatedResult && (
            <div className="p-4 rounded-2xl bg-emerald-950/90 border border-emerald-600 text-emerald-100 space-y-1.5">
              <div className="font-extrabold text-emerald-300 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                <span>Professional Account Activated ({activatedResult.applicationReference})</span>
              </div>
              <p>
                <strong>{activatedResult.fullName}</strong> ({activatedResult.email}) is now an active, verified professional. Identity &amp; utility documents remain exclusively in your email archive.
              </p>
              {activatedResult.temporaryPassword && (
                <p className="font-mono text-xs bg-slate-900 px-3 py-1.5 rounded-lg border border-emerald-700 inline-block">
                  Temporary Password: <strong>{activatedResult.temporaryPassword}</strong>
                </p>
              )}
            </div>
          )}

          {mode === 'token' ? (
            <div>
              <label className="block font-bold text-slate-300 mb-1">
                Signed Activation Token (from Section 5 of the Verification Notification) *
              </label>
              <textarea
                rows={3}
                value={activationToken}
                onChange={(e) => setActivationToken(e.target.value)}
                placeholder="Paste the base64url.hmac token from the email dossier..."
                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-emerald-300 font-mono text-xs focus:outline-hidden focus:border-emerald-500"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Full Name *</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">Primary Trade *</label>
                <select
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                >
                  {SERVICE_CATEGORIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">State *</label>
                <select
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    const lgas = NIGERIAN_LGAS_MAP[e.target.value] || [];
                    if (lgas[0]) setLga(lgas[0]);
                  }}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                >
                  {NIGERIAN_STATES.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-300 font-bold mb-1">LGA *</label>
                <select
                  value={lga}
                  onChange={(e) => setLga(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                >
                  {(NIGERIAN_LGAS_MAP[state] || ['Ikeja']).map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div>
              <label className="block text-slate-300 font-bold mb-1">
                Upload Approved Public Profile Photograph (from Attachment #1 in Email)
              </label>
              <label className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 font-semibold inline-flex items-center gap-2 cursor-pointer">
                <Upload className="w-3.5 h-3.5 text-emerald-400" />
                <span>{approvedPhotoName || 'Select 01_profile_photo image...'}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </label>
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">
                Initial / Temporary Sign-In Password (Optional — auto-generated if blank)
              </label>
              <div className="relative">
                <KeyRound className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  value={temporaryPassword}
                  onChange={(e) => setTemporaryPassword(e.target.value)}
                  placeholder="Leave blank to auto-generate strong password"
                  className="w-full pl-8 pr-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Activating Approved Professional...</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>Approve &amp; Activate Professional Account</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
